import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { computeBreakdown, formatMoney } from "./calculations";
import { convert } from "./fx";
import { getCurrencySymbol } from "./constants";

function buildRows(template, ratesObj) {
  const symbol = getCurrencySymbol(template.displayCurrency);
  const fromCcy = template.inputCurrency;
  const toCcy = template.displayCurrency;

  return template.products.map((p) => {
    const b = computeBreakdown(p, template.costLines, template.margin);
    const conv = (v) => convert(v, fromCcy, toCcy, ratesObj);

    const row = {
      Product: p.name || "Untitled",
      Quantity: b.quantity,
      [`Cost (${fromCcy})`]: Number(p.cost) || 0,
      [`Cost (${toCcy})`]: Number(conv(b.cost).toFixed(2)),
    };
    b.lines.forEach((l) => {
      const lbl = l.type === "percent" ? `${l.label} (${l.value}%)` : `${l.label}`;
      row[`${lbl} ${toCcy}`] = Number(conv(l.amount).toFixed(2));
    });
    row[`Landed Cost ${toCcy}`] = Number(conv(b.totalCostBeforeMargin).toFixed(2));
    row[`Margin (${template.margin}%) ${toCcy}`] = Number(conv(b.marginAmount).toFixed(2));
    row[`Selling Price/Unit ${toCcy}`] = Number(conv(b.sellingPricePerUnit).toFixed(2));
    row[`Total Selling ${toCcy}`] = Number(conv(b.totalSellingPrice).toFixed(2));
    return row;
  });
}

export function exportCSV(template, ratesObj) {
  const rows = buildRows(template, ratesObj);
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = r[h];
          if (typeof v === "string" && (v.includes(",") || v.includes('"'))) {
            return `"${v.replace(/"/g, '""')}"`;
          }
          return v;
        })
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  download(blob, `${template.name || "pricing"}.csv`);
}

export function exportXLSX(template, ratesObj) {
  const rows = buildRows(template, ratesObj);
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Pricing");
  XLSX.writeFile(wb, `${template.name || "pricing"}.xlsx`);
}

export function exportPDF(template, ratesObj) {
  const rows = buildRows(template, ratesObj);
  if (rows.length === 0) return;
  const doc = new jsPDF({ orientation: "landscape" });
  const symbol = getCurrencySymbol(template.displayCurrency);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(template.name || "Pricing Template", 14, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(
    `Input: ${template.inputCurrency}  →  Display: ${template.displayCurrency}   |   Margin: ${template.margin}%   |   Generated: ${new Date().toLocaleString()}`,
    14,
    22
  );

  const headers = Object.keys(rows[0]);
  const body = rows.map((r) =>
    headers.map((h) => (typeof r[h] === "number" ? r[h].toFixed(2) : r[h]))
  );

  autoTable(doc, {
    head: [headers],
    body,
    startY: 28,
    styles: { fontSize: 7, cellPadding: 2, lineColor: [229, 229, 229], lineWidth: 0.1 },
    headStyles: { fillColor: [17, 17, 17], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    theme: "grid",
  });

  doc.save(`${template.name || "pricing"}.pdf`);
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
